import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { catchError, finalize, map, of, switchMap } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { DentistService } from '../../../../services/dentist';
import { JobApplicationService } from '../../../../services/job-application';
import { LoginService } from '../../../../services/login.service';

@Component({ selector: 'app-applyfordentist', templateUrl: './applyfordentist.component.html', styleUrl: './applyfordentist.component.scss' })
export class ApplyfordentistComponent implements OnInit {
  form!: FormGroup;
  @Input() jobId: string | number | null = null;
  @Output() applicationSubmitted = new EventEmitter<any>();
  isSubmitting = false;
  errorMessage = '';
  successMessage = '';
  selectedCv: File | null = null;
  selectedImage: File | null = null;
  certificates: string[] = [];
  currentStep = 0;
  readonly steps = [1, 2, 3];

  readonly specialties = ['General Dentistry', 'Family Dentistry', 'Endodontics', 'Prosthodontics', 'Periodontics', 'Orthodontics', 'Oral & Maxillofacial Surgery (OMFS)', 'Oral Surgery', 'Pediatric Dentistry', 'Restorative Dentistry', 'Oral Medicine / Oral Pathology', 'Other'];
  readonly qualifications = ['Bachelor Degree (BDS / DDS)', 'Master’s Degree', 'Fellowship/Board Certification', 'PhD', 'PhD + Fellowship', 'Other'];
  readonly certificateOptions = ['Endodontics', 'Fixed Prosthodontics', 'Implant Prosthodontics', 'Implant Surgery', 'Digital Dentistry', 'Esthetic Dentistry'];
  // These labels are the exact values accepted by PrimaryRegistrationController.
  readonly classificationLevels = [
    { value: 'General Dentist', en: 'General dentist', ar: 'طبيب أسنان عام' },
    { value: 'Registrar', en: 'Registrar', ar: 'مسجل' },
    { value: 'Resident', en: 'Resident', ar: 'طبيب مقيم' },
    { value: 'Senior Registrar', en: 'Senior registrar', ar: 'مسجل أول' },
    { value: 'Consultant', en: 'Consultant', ar: 'استشاري' }
  ];

  constructor(private fb: FormBuilder, private route: ActivatedRoute, private router: Router, private dentistService: DentistService, private applicationService: JobApplicationService, private loginService: LoginService, private transloco: TranslocoService) {}

  ngOnInit(): void {
    this.jobId = this.jobId || this.route.snapshot.paramMap.get('id');
    this.form = this.fb.group({
      full_name: ['', Validators.required], date_of_birth: [''], gender: [''], mobile: ['', Validators.required], email: ['', Validators.email], whatsapp_number: [''],
      specialty: ['', Validators.required], specialty_other: [''], highest_qualification: ['', Validators.required], has_additional_certificates: [false],
      scfhs_status: [''], scfhs_classification_level: [''], scfhs_classification_date: [''], years_of_practice: [''], preferred_employment_type: [''], preferred_practice_environment: [''], portfolio_link: [''], additional_notes: ['']
    });

  }

  submit(): void {
    this.errorMessage = '';
    if (this.form.invalid || !this.jobId || this.isSubmitting) { this.form.markAllAsTouched(); return; }
    this.isSubmitting = true;
    this.dentistService.create(this.buildFormData()).pipe(
      switchMap((res: any) => {
        const dentistId = res?.data?.id || res?.id;
        if (!dentistId) throw new Error(this.t('requestFailed'));
        return this.applicationService.publicApplyToJob({
          job_id: this.jobId,
          dentist_id: dentistId,
          applicant_name: this.form.value.full_name.trim(),
          applicant_phone: this.form.value.mobile.trim(),
          applicant_email: this.form.value.email?.trim(),
          cv: this.selectedCv
        }).pipe(
          switchMap((applicationResponse: any) => {
            const credentials = applicationResponse?.data?.login_data;
            if (!credentials?.username || !credentials?.password) {
              return of({ applicationResponse, loginResponse: null });
            }

            return this.loginService.user_login(credentials).pipe(
              map((loginResponse: any) => ({
                applicationResponse,
                loginResponse: this.normalizeLoginResponse(loginResponse)
              })),
              // The application has already succeeded; do not present a login
              // failure as a failed application submission.
              catchError(() => of({ applicationResponse, loginResponse: null }))
            );
          })
        );
      }),
      finalize(() => this.isSubmitting = false)
    ).subscribe({
      next: ({ applicationResponse, loginResponse }: any) => {
        if (loginResponse?.token) this.saveLoginSession(loginResponse);
        this.successMessage = this.t('success');
        this.applicationSubmitted.emit(applicationResponse);
        setTimeout(() => this.router.navigate([loginResponse?.token ? '/Home' : '/jobs']), 1200);
      },
      error: (err: any) => {
        const fieldErrors = err?.error?.errors || {};
        if (fieldErrors.scfhs_classification_level) this.currentStep = 0;
        this.errorMessage = err?.error?.message || err?.message || this.t('requestFailed');
      }
    });
  }

  onCvChange(event: Event): void { const file = (event.target as HTMLInputElement).files?.[0] || null; this.selectedCv = file?.type === 'application/pdf' ? file : null; if (file && !this.selectedCv) this.errorMessage = this.t('cvInvalid'); }
  onImageChange(event: Event): void { const file = (event.target as HTMLInputElement).files?.[0] || null; this.selectedImage = file?.type.startsWith('image/') ? file : null; }
  toggleCertificate(value: string, checked: boolean): void { this.certificates = checked ? [...this.certificates, value] : this.certificates.filter(item => item !== value); }
  isSurgery(): boolean { const value = this.form?.value?.specialty; return value === 'Oral Surgery' || value === 'Oral & Maxillofacial Surgery (OMFS)'; }
  isInvalid(name: string): boolean { const control = this.form.get(name); return Boolean(control?.invalid && (control.touched || control.dirty)); }
  get isAr(): boolean { return this.transloco.getActiveLang().startsWith('ar'); }
  back(): void { this.router.navigate(['/jobs']); }
  nextStep(): void {
    if (this.currentStep === 0 && (this.form.get('full_name')?.invalid || this.form.get('mobile')?.invalid || this.form.get('specialty')?.invalid || this.form.get('highest_qualification')?.invalid)) {
      this.form.get('full_name')?.markAsTouched();
      this.form.get('mobile')?.markAsTouched();
      this.form.get('specialty')?.markAsTouched();
      this.form.get('highest_qualification')?.markAsTouched();
      return;
    }
    this.currentStep = Math.min(this.currentStep + 1, this.steps.length - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  previousStep(): void { this.currentStep = Math.max(this.currentStep - 1, 0); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  getStepLabel(index: number): string {
    const labels = [
      this.isAr ? 'البيانات' : 'Data',
      this.isAr ? 'المستندات' : 'Documents',
      this.isAr ? 'السيرة الذاتية' : 'CV'
    ];
    return labels[index];
  }

  private buildFormData(): FormData {
    const fd = new FormData();
    Object.entries(this.form.getRawValue()).forEach(([key, value]) => {
      if (typeof value === 'boolean') fd.append(key, value ? '1' : '0');
      else if (value) fd.append(key, String(value));
    });
    this.certificates.forEach(value => fd.append('additional_certificates[]', value));
    if (this.selectedCv) fd.append('cv_pdf', this.selectedCv);
    if (this.selectedImage) fd.append('doctor_image', this.selectedImage);
    return fd;
  }
  private saveLoginSession(response: any): void {
    const user = response.user || {};
    this.loginService.setToken(response.token);
    localStorage.setItem('user', JSON.stringify(user));
    if (user.username) localStorage.setItem('username', user.username);
    if (user.id !== undefined && user.id !== null) localStorage.setItem('user_id', String(user.id));
    if (user.user_type) localStorage.setItem('user_type', user.user_type);
    if (user.type !== undefined && user.type !== null) localStorage.setItem('type', String(user.type));
    if (user.phone) localStorage.setItem('phone', user.phone);
    if (user.email) localStorage.setItem('email', user.email);
  }
  private normalizeLoginResponse(response: any): any {
    return {
      token: response?.token || response?.data?.token,
      user: response?.user || response?.data?.user
    };
  }
  private t(key: string): string { return this.transloco.translate(`atw.dentistApply.${key}`); }
}
